/**
 * Energy formatting utilities (stored or consumed energy)
 * For power (rate of energy), use power.ts
 * For electrical units (voltage, current), use voltage.ts and current.ts
 */

import { isNil } from "./number";
import { FormatRegistry } from "./registry";
import type { EnergyFormat, FieldValue, FormattedValue } from "./types";

/**
 * Format as Watt-hours
 */
export const toWh = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${value.toFixed(decimals)} Wh`;
};

/**
 * Format as kilowatt-hours
 */
export const tokWh = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${(value / 1000).toFixed(decimals)} kWh`;
};

/**
 * Format as megawatt-hours
 */
export const toMWh = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${(value / 1_000_000).toFixed(decimals)} MWh`;
};

/**
 * Format as gigawatt-hours
 */
export const toGWh = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${(value / 1_000_000_000).toFixed(decimals)} GWh`;
};

/**
 * Auto-scale energy to the most appropriate unit
 */
export const autoScaleEnergy = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;

  const absValue = Math.abs(value);

  if (absValue >= 1_000_000_000) {
    return toGWh(value, decimals);
  } else if (absValue >= 1_000_000) {
    return toMWh(value, decimals);
  } else if (absValue >= 1000) {
    return tokWh(value, decimals);
  } else {
    return toWh(value, decimals);
  }
};

// Power formatting has been moved to power.ts

/**
 * Format energy according to EnergyFormat specification
 */
export const formatEnergy = (value: FieldValue, format: EnergyFormat): FormattedValue => {
  if (format.hideWhenNull && (value === null || value === undefined)) return null;
  if (format.hideWhenZero && value === 0) return null;
  if (value == null) return format.nullText ?? null;

  const numValue = typeof value === "string" ? parseFloat(value) : Number(value);
  if (isNaN(numValue)) return format.nullText ?? null;

  const decimals = format.decimals ?? 1;

  // If a specific unit is provided, use it
  if (format.unit) {
    switch (format.unit) {
      case "GWh":
        return toGWh(numValue, decimals);
      case "MWh":
        return toMWh(numValue, decimals);
      case "kWh":
        return tokWh(numValue, decimals);
      case "Wh":
        return toWh(numValue, decimals);
    }
  }

  // Default to auto-scaling unless explicitly disabled
  if (format.autoScale !== false) {
    return autoScaleEnergy(numValue, decimals);
  }

  // If auto-scale is explicitly false and no unit specified, use base Wh
  return toWh(numValue, decimals);
};

// Electrical units have been moved to voltage.ts and current.ts
// Resistance units (Ohms) could be moved to a separate resistance.ts if needed

// Register energy and power formatters
FormatRegistry.register("kWh", (value) => {
  if (typeof value === "number") return tokWh(value);
  return null;
});

// Power formatters are now in power.ts

FormatRegistry.register("autoScaleEnergy", (value, decimals) => {
  if (typeof value === "number") {
    return autoScaleEnergy(value, typeof decimals === "number" ? decimals : 1);
  }
  return null;
});

// Auto-scale power is now in power.ts

// Register the energy formatter
// biome-ignore lint/suspicious/noExplicitAny: Required for FormatRegistry type compatibility
FormatRegistry.register("energy", formatEnergy as any);
